// A month's bookings: loading them, the shape the bookings page works
// with, and the four things staff do to one (create, edit, change status,
// complete) with how each leaves the loaded list. No rendering.
//
// Every function takes the bookings API as `api`, defaulting to the real
// one, so the module is tested with a fake and nothing mocked.

import * as bookingsApi from '../../api/bookings'
import { normalizeContactTags } from '../../utils/contactTags'
import { BOOKING_STATUS, fromServerStatus, toServerStatus } from './bookingStatus'

const DEFAULT_TIME_SLOT = '19:00'

function timeSlot(value) {
  return typeof value === 'string' && value ? value.slice(0, 5) : DEFAULT_TIME_SLOT
}

/**
 * A server booking in the shape the page works with.
 *
 * Reads the fields the manager API returns, by the one name each has:
 * the monthly list (`ManagerBookingMonthlyItem`) and the full Booking
 * Engine row that create and edit answer with. `source` is the booking's
 * channel and `contact_tags` the Contact's tags, both as the server sent
 * them; a booking with no source has none.
 */
export function normalizeBooking(raw, fallbackDate) {
  return {
    id: raw.id,
    order_id: raw.order_id || null,
    booking_code: raw.booking_code || null,
    customer_name: raw.customer_name || '',
    customer_phone: raw.customer_phone || '',
    status: fromServerStatus(raw.status),
    time_slot: timeSlot(raw.time_slot),
    date: raw.date || fallbackDate,
    guests: raw.party_size ?? 1,
    service_ref: raw.service_ref || '',
    duration_minutes: raw.duration_minutes ?? null,
    staff_label: raw.staff_label || '',
    source: raw.channel || '',
    notes: raw.notes || '',
    contact_tags: normalizeContactTags(raw.contact_tags),
  }
}

function replaceBooking(bookings, booking) {
  return bookings.map((item) => (item.id === booking.id ? booking : item))
}

/**
 * Load a month (`YYYY-MM`) of a tenant's bookings.
 *
 * Resolves to `{ ok: true, bookings }`, or `{ ok: false, error, bookings: [] }`
 * when the load failed — a failure is never an empty month.
 */
export async function loadMonth({ tenantId, month, fallbackDate, api = bookingsApi }) {
  try {
    const data = await api.listMonthlyBookings(tenantId, month)
    const rows = Array.isArray(data?.bookings) ? data.bookings : []
    return { ok: true, bookings: rows.map((row) => normalizeBooking(row, fallbackDate)) }
  } catch (error) {
    return { ok: false, error, bookings: [] }
  }
}

/**
 * Create a booking. Resolves to `{ booking, bookings }` with the new
 * booking appended to the list. `contactTags` are the tags staff picked in
 * the form: the create response is the booking row and does not carry the
 * Contact's tags. Rejects with the API error, leaving the list as it was.
 */
export async function createBooking({ tenantId, payload, contactTags = [], bookings, fallbackDate, api = bookingsApi }) {
  const created = await api.createBooking(tenantId, payload)
  const booking = normalizeBooking({ ...created, contact_tags: contactTags }, fallbackDate)
  return { booking, bookings: [...bookings, booking] }
}

/**
 * Change a booking's details. Resolves to `{ booking, bookings }` with the
 * booking replaced by the server's row; the Contact's tags, which an edit
 * cannot change and the response does not carry, are kept. Rejects with
 * the API error, leaving the list as it was.
 */
export async function updateBookingDetails({ tenantId, booking, payload, bookings, fallbackDate, api = bookingsApi }) {
  const updated = await api.updateBooking(tenantId, booking.id, payload)
  const next = {
    ...normalizeBooking(updated, fallbackDate),
    contact_tags: booking.contact_tags,
    order_id: booking.order_id,
  }
  return { booking: next, bookings: replaceBooking(bookings, next) }
}

/**
 * Move a booking to a status. Completing goes through the complete
 * action, which has a time guard and effects a status change has not; any
 * other status, a tenant's custom ones included, is a status change.
 *
 * Whatever the status, the list is then brought up to date the same way:
 * the month is loaded again, since a status change can alter more than the
 * status (confirming issues the booking code). If that reload fails, the
 * change has still happened, so the list is returned with just the status
 * updated. Resolves to `{ booking, bookings }`. Rejects with the API error
 * when the change itself is refused, leaving the list as it was.
 */
export async function changeBookingStatus({ tenantId, bookingId, status, month, bookings, fallbackDate, api = bookingsApi }) {
  const target = toServerStatus(status)
  if (target === BOOKING_STATUS.completed) {
    await api.completeBooking(tenantId, bookingId)
  } else {
    await api.updateBookingStatus(tenantId, bookingId, target)
  }

  const reloaded = await loadMonth({ tenantId, month, fallbackDate, api })
  const next = reloaded.ok
    ? reloaded.bookings
    : bookings.map((item) => (item.id === bookingId ? { ...item, status: fromServerStatus(target) } : item))
  return { booking: next.find((item) => item.id === bookingId) || null, bookings: next }
}
